import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const HOVER_CLOSE_DELAY_MS = 250;
const MAIN_MENU_SWITCH_DELAY_MS = 0;
const OVERFLOW_SWITCH_DELAY_MS = 100;
const SUBMENU_APPROACH_SWITCH_DELAY_MS = 600;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #activationTimer;

  /** @type {{ x: number, y: number } | null} */
  #activePointerPosition = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('pointerover', this.activate);
    this.addEventListener('pointerout', this.deactivate);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('pointerover', this.activate);
    this.removeEventListener('pointerout', this.deactivate);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#cancelActivation();
    this.#cancelClose();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#scheduleClose();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Handle delegated pointer activation and immediate focus activation.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    const listItem = findMenuListItem(event);
    if (!listItem) {
      if (event.type === 'pointerover' && this.overflowMenu?.matches(':hover')) this.#cancelClose();
      return;
    }

    if (event.type === 'pointerover') {
      const linkTitle = event
        .composedPath()
        .find((element) => element instanceof Element && element.matches('.menu-list__link-title'));

      if (!(linkTitle instanceof Element)) {
        this.#cancelActivation();
        this.#cancelClose();
        return;
      }

      if (event.relatedTarget instanceof Node && linkTitle.contains(event.relatedTarget)) {
        this.#cancelClose();
        return;
      }

      this.#cancelActivation();
      this.#cancelClose();
      this.#scheduleActivation(listItem, linkTitle, this.#getSwitchDelay(event, listItem), {
        x: event.clientX,
        y: event.clientY,
      });
      return;
    }

    this.#cancelActivation();
    this.#cancelClose();
    this.#activateListItem(listItem, null);
  };

  /**
   * Activate the selected list item.
   * @param {HTMLElement | null} listItem
   * @param {{ x: number, y: number } | null} pointerPosition
   */
  #activateListItem(listItem, pointerPosition) {
    if (!listItem || !this.headerComponent) return;

    const item = findMenuItem(listItem);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = listItem.slot === '';

    this.#activateItem(item, isDefaultSlot);
    this.#activePointerPosition = pointerPosition;
  }

  /**
   * Commit a menu item activation.
   * @param {HTMLElement} item
   * @param {boolean} isDefaultSlot
   */
  #activateItem(item, isDefaultSlot) {
    this.#cancelClose();

    if (item == this.#state.activeItem) return;

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * Delay only when the pointer is travelling downward toward the open submenu.
   * @param {PointerEvent} event
   * @param {HTMLElement} listItem
   */
  #getSwitchDelay(event, listItem) {
    if (listItem.slot === 'overflow') return OVERFLOW_SWITCH_DELAY_MS;

    const activeSubmenu = findSubmenu(this.#state.activeItem);
    const isReturningFromSubmenu =
      event.relatedTarget instanceof Node && Boolean(activeSubmenu?.contains(event.relatedTarget));

    if (isReturningFromSubmenu) return MAIN_MENU_SWITCH_DELAY_MS;

    const hasActiveSubmenu = Boolean(activeSubmenu) || this.dataset.overflowExpanded === 'true';
    const origin = this.#activePointerPosition;

    if (!hasActiveSubmenu || !origin) return MAIN_MENU_SWITCH_DELAY_MS;

    const horizontalDistance = Math.abs(event.clientX - origin.x);
    const downwardDistance = event.clientY - origin.y;
    const isMovingTowardSubmenu =
      downwardDistance >= 8 &&
      (horizontalDistance === 0 || downwardDistance / horizontalDistance >= 0.2);

    return isMovingTowardSubmenu ? SUBMENU_APPROACH_SWITCH_DELAY_MS : MAIN_MENU_SWITCH_DELAY_MS;
  }

  /**
   * Delay item activation just long enough to ignore incidental pointer flyovers.
   * @param {HTMLElement} listItem
   * @param {Element} linkTitle
   * @param {number} delay
   * @param {{ x: number, y: number }} pointerPosition
   */
  #scheduleActivation(listItem, linkTitle, delay, pointerPosition) {
    const item = findMenuItem(listItem);
    if (!item || item == this.#state.activeItem) return;

    if (delay === 0) {
      this.#activateListItem(listItem, pointerPosition);
      return;
    }

    this.#activationTimer = setTimeout(() => {
      this.#activationTimer = undefined;

      if (!linkTitle.matches(':hover')) return;

      this.#activateListItem(listItem, pointerPosition);
    }, delay);
  }

  #cancelActivation() {
    clearTimeout(this.#activationTimer);
    this.#activationTimer = undefined;
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.type === 'pointerout') {
      const listItem = findMenuListItem(event);
      if (!listItem) return;
      if (event.relatedTarget instanceof Node && listItem.contains(event.relatedTarget)) return;
    }

    this.#cancelActivation();

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      this.#cancelClose();
      return;
    }

    if (event.type.startsWith('pointer')) {
      this.#scheduleClose();
      return;
    }

    this.#deactivate();
  };

  #scheduleClose() {
    this.#cancelClose();
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      this.#deactivate();
    }, HOVER_CLOSE_DELAY_MS);
  }

  #cancelClose() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    this.#cancelActivation();
    this.#cancelClose();

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.#activePointerPosition = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element.querySelector('[ref="menuitem"]');
}

/**
 * Find the menu list item associated with a delegated pointer or proxied focus event.
 * @param {Event} event
 * @returns {HTMLElement | null}
 */
function findMenuListItem(event) {
  const listItem = event.composedPath().find(
    (element) => element instanceof HTMLElement && element.matches('.menu-list__list-item')
  );

  if (listItem instanceof HTMLElement) return listItem;

  return event.target instanceof Element ? event.target.closest('.menu-list__list-item') : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
